import { Component, OnInit } from '@angular/core';
import { ClientIframeProductModel, ClientPackageModel, EPriceType, EWishlistItemType, IframeCategoryItem, ProductListItemModelCPB } from '@atlas-workspace/shared/models';
import { delay, filter, finalize, take, tap } from 'rxjs';

import { OptionsIframeService } from '../services/options-iframe.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router } from '@angular/router';

@UntilDestroy()
@Component({
  selector: 'atl-iframe-summary',
  templateUrl: './iframe-summary.component.html',
  styleUrls: ['./iframe-summary.component.scss'],
})
export class OptionsIframeSummaryComponent implements OnInit {
  public totalPrice = 0;
  public categories: IframeCategoryItem[] = [];
  public showPrices = true;
  loading = true;
  readonly wishlistItemType = EWishlistItemType;
  readonly priceType = EPriceType;
  readonly truncateLimit = 50;
  private selectionToken!: string;
  private tokenId!: string;

  public savedCategories: any;

  constructor(private readonly optionsIframeService: OptionsIframeService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.tokenId = this.route?.parent?.snapshot.params['tokenId'];
    this.setupSelectionTokenStream();
    this.getCategories();
    this.getShowPrices();
    this.getPackages();
  }

  private setupSelectionTokenStream(): void {
    this.optionsIframeService.selectionToken$.pipe(
      filter((token): token is string => {
        return !!token;
      }),
      tap(token => {
        this.selectionToken = token;
        this.getIframeSelectedItems();
      }),
      untilDestroyed(this)
    ).subscribe();
  }
  

  private getIframeSelectedItems(): void {
     this.optionsIframeService.getIframeSelectedItems(this.tokenId, this.selectionToken)
     .pipe(take(1))
     .subscribe((selectedProducts: ClientIframeProductModel[]) => {
      const groupedByCategory = selectedProducts.reduce((acc: any, product) => {
        const categoryId = product.category.id;
      
        if (!acc[categoryId]) {
          acc[categoryId] = {
            category: product.category,
            products: [],
            totalPrice: 0,
          };
        }
      
        acc[categoryId].products.push(product);
        acc[categoryId].totalPrice += product.fullPrice;
      
        return acc;
      }, {});
      
      const groupedArray = Object.values(groupedByCategory);
      this.savedCategories = groupedArray;
      this.totalPrice = this.savedCategories.reduce((total: any, x: any) => total + Number(x.totalPrice), 0);
      this.optionsIframeService.setTotalPrice(this.totalPrice);
      this.savedCategories.forEach((cat: any) => {
       const index =  this.categories.findIndex(x => x.id === cat.category.id);

       if (~index) {
        this.categories[index].products = cat.products;
        this.categories[index].totalPrice = cat.totalPrice;
       }
      });
      this.optionsIframeService.updateCategories(this.categories); 
     });
  }

  private getCategories(): void {
    this.optionsIframeService.categoriesData$.pipe(
      filter(x => !!x.length),
      take(1),
    )
      .subscribe((res: IframeCategoryItem[]) => {
        this.categories = res.filter(x => !x.is_introduction);
        this.totalPrice = res.reduce((total, x) => total + Number(x.totalPrice || 0), 0);
      });
  }

  private getShowPrices(): void {
    this.optionsIframeService.showPrices$.pipe(untilDestroyed(this))
      .subscribe((showPrices: boolean) => {
        this.showPrices = showPrices;
      });
  }

  private getPackages(): void {
    this.optionsIframeService.packages$.pipe(untilDestroyed(this), delay(200))
      .subscribe((res => {
        if (res && res.packages.length) {
          const selectedPackage = res.packages.find(x => x.selected);
          if (selectedPackage) {
            if (res.optionDesignLineProducts) {
              selectedPackage.categories.forEach(category => {
                category.selectedProducts = category.products.filter(product => product.isDefault);
              });
              this.filterCategoriesByDesignLines(selectedPackage);
            } else {
              this.filterCategoriesByRegularPackage(selectedPackage);
            }
          } else {
            if (res.optionDesignLine && !res.optionDesignLineProducts) {
              this.router.navigate(['../packages'], { relativeTo: this.route });
            }
          }
        }

        this.loading = false;
      }));
  }


  private filterCategoriesByDesignLines(selectedPackage: ClientPackageModel): void {
    const selectedCategoryMap = new Map(
      selectedPackage.categories?.map((cat) => [cat.id, cat])
    );

    const updatedCategories = this.categories
      .filter((category) => selectedCategoryMap.has(category.id))
      .map((category) => {
        const selectedCategory = selectedCategoryMap.get(category.id);

        if (!selectedCategory) return category;

        return {
          ...category,
          products: selectedCategory.selectedProducts || [],
        };
      });

    this.categories = updatedCategories;
  }

  private filterCategoriesByRegularPackage(selectedPackage: ClientPackageModel): void {
    const selectedProductsByLayoutId = new Map<number, ProductListItemModelCPB>();

    for (const selectedCategory of selectedPackage.categories || []) {
      for (const product of selectedCategory.products || []) {
        selectedProductsByLayoutId.set(product.layoutProductTypeId, product);
      }
    }

    const updatedCategories = this.categories.map((category) => {
      const updatedProducts = category.products.map((product) => {
        const replacement = selectedProductsByLayoutId.get(product.layoutProductTypeId);
        return replacement ? replacement : product;
      });

      return {
        ...category,
        products: updatedProducts,
      };
    });

    this.categories = updatedCategories;
  }




}
